import React, { FC, ReactElement, useContext, useRef } from 'react';
import { DatesHeaderProps } from './datesHeader.types';
import { StyledDatesHeaderDiv, StyledDateBoxDiv } from './datesHeader.styles';
import { ContentContext } from '../../index.contexts';
import { formatDateStringBasedOnLocale } from '../../../../../../utils/src/helper';
import { AR_LOCALE, HI_LOCALE } from '../../../../../../modules/constants/Calendar.constants';

const DatesHeader: FC<DatesHeaderProps> = ({
  checkInDate,
  checkOutDate,
  onClearCheckIn,
  onClearCheckOut,
  focusedCheckIn,
  onClickCheckIn,
  onClickCheckOut,
}) => {
  const { checkInLabel, checkOutLabel } = useContext(ContentContext);

  /**
   * Return react element to display check in/out date and clear button
   * @param label
   * @param date
   * @param onClearHandler
   * @param onClickHandler
   * @param focused
   */
  const DateBox = (
    label: string,
    date: string,
    onClearHandler: () => void,
    onClickHandler: () => void,
    focused: boolean
  ): ReactElement => {
    const { datesPlaceholderText, clear } = useContext(ContentContext);
    const mainBtn = useRef<HTMLButtonElement>(null);
    const clickTrackingLoc = 'Search Form';
    date = formatDateStringBasedOnLocale(date);
    // handler for clear button click
    const clearClickHandler = (): void => {
      setTimeout(onClearHandler, 0);
      if (window.navigator?.platform?.indexOf('Win') > -1) {
        mainBtn.current?.setAttribute('aria-live', 'polite');
      }
      mainBtn.current?.focus();
    };

    if (focused) {
      mainBtn.current?.focus();
    }

    return (
      <StyledDateBoxDiv
        data-component-name="a-shop-DatesHeader"
        data-testid="shop-DatesHeader"
        className={focused ? 'shop-button-focus' : ''}
      >
        <button
          type="button"
          ref={mainBtn}
          aria-expanded={focused}
          aria-live={focused ? 'polite' : 'off'}
          className="custom_click_track"
          {...{ custom_click_track_value: `${clickTrackingLoc}| Date Picker ${label} button |internal` }}
          onClick={onClickHandler}
        >
          <span className="t-label-xs dates-header-label">{label}</span>
          <span className={`h-r-form-field-txt ${!date ? 'gray-shade' : ''}`}>
            {date ? date : datesPlaceholderText}
          </span>
        </button>
        {focused && date ? (
          <button
            type="button"
            className="link-white icon-cancel cancel-button custom_click_track"
            {...{ custom_click_track_value: `${clickTrackingLoc}| Date Picker ${label} clear button |internal` }}
            onClick={clearClickHandler}
          >
            <span className="sr-only">{clear}</span>
          </button>
        ) : (
          ''
        )}
      </StyledDateBoxDiv>
    );
  };

  return (
    <StyledDatesHeaderDiv>
      {DateBox(checkInLabel, checkInDate, onClearCheckIn, onClickCheckIn, focusedCheckIn)}
      {DateBox(checkOutLabel, checkOutDate, onClearCheckOut, onClickCheckOut, !focusedCheckIn)}
    </StyledDatesHeaderDiv>
  );
};

export default DatesHeader;
